import React from 'react';
import isEqual from 'lodash/isEqual';
import {
  Alert,
  Box,
  Card,
  Flex,
  FormError,
  FormHelperText,
  Heading,
  Icon,
  Link,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
  Tooltip,
  TooltipProps,
} from 'pounceV2';
import FormikEditorV3 from 'Components/fields/Editor/EditorV3';
import { Link as RRLink } from 'react-router-dom';
import { FastField, useFormikContext } from 'formik';
import urls from 'Source/urls';
import { RULE_FILTERS_DOC_URL, WRITING_CORRELATION_RULES_DOC_URL } from 'Source/constants';
import { DetectionType } from 'Generated/schema';
import { CompoundInputFieldArrayMetaProps } from 'Components/forms/BaseDetectionForm/CompoundInputFieldArray/CompoundInputFieldArrayV3';
import { ExtractedFilterCondition } from 'Components/forms/DetectionFieldFiltersForm/constants';
import FormikFilterFieldComboboxV3 from 'Components/forms/DetectionFieldFiltersForm/FilterFieldComboBox/FormikFilterFieldComboboxV3';
import FilterOperatorComboboxV3 from 'Components/forms/DetectionFieldFiltersForm/FilterOperatorComboBox/FilterOperatorComboboxV3';
import FilterValueFieldV3 from 'Components/forms/DetectionFieldFiltersForm/FilterValueField/FilterValueFieldV3';
import { useGetFlatFilterFields } from 'Source/graphql/queries/getFlatFilterFields.generated';
import CompoundInputFieldArrayWithJoinsV3 from 'Components/forms/BaseDetectionForm/CompoundInputFieldArray/CompoundInputFieldArrayWithJoinsV3';
import DetectionFieldFiltersFormV3 from 'Components/forms/DetectionFieldFiltersForm/DetectionFieldFiltersFormV3';
import {
  analysisTypeToLabel,
  findMaximumDepth,
  formatDSFAsFilters,
  formatFiltersAsDSF,
} from 'Pages/Build/Detections/utils';
import useUrlParams from 'Hooks/useUrlParams';
import CorrelationRuleEditorV3 from 'Pages/Build/Detections/CorrelationRules/CorrelationRuleEditorV3';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';
import { SimplePolicyFormValues } from 'Pages/Build/Detections/Policies/SimplePolicyForm/SimplePolicyFormV3';
import { useTheme } from '@mui/material';

const MAXIMUM_DEPTH = 3;

type TooltipToggleButtonProps = Partial<Omit<TooltipProps, 'children'>> & ToggleButtonProps;
const TooltipToggleButton: React.FC<TooltipToggleButtonProps> = ({
  content,
  wrapper,
  alignment,
  ...rest
}) =>
  content ? (
    <Tooltip content={content} wrapper={wrapper} alignment={alignment}>
      <ToggleButton {...rest} />
    </Tooltip>
  ) : (
    <ToggleButton {...rest} />
  );

interface BaseSimpleDetectionFormEditorSectionProps {
  type: DetectionType;
  isUpdate?: boolean;
  isManaged: boolean;
  canEdit?: boolean;
}
type CloneParams = { relatedDetectionId?: string };

const BaseSimpleDetectionFormEditorSectionV3: React.FC<
  BaseSimpleDetectionFormEditorSectionProps
> = ({ type, isUpdate, isManaged, canEdit = true }) => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const { urlParams } = useUrlParams<CloneParams>();
  const { values, errors, setFieldValue } = useFormikContext<
    SimpleRuleFormValues | SimplePolicyFormValues
  >();
  const {
    // @ts-ignore
    simpleDetectionBody,
    // @ts-ignore
    simpleDetectionBodyGUI,
    // @ts-ignore
    logTypes,
    // @ts-ignore
    isSimpleDetection = false,
    // @ts-ignore
    isConsoleCompatible = true,
  } = values;
  /*
    Some simple detections are, as of this writing, not expressible in the Rule Builder. An example
    is detections using "DeepKey". While the RuleBuilder was envisioned as a primary way to build simple
    detections, it is not, in fact, the only way. So until RB has parity with other tooling, we need this so we
    can switch to the text editor smartly.
  */
  const hideRuleBuilder = (simpleDetectionBodyGUI?.ruleBuilderCompatible ?? true) === false;
  const [yamlParseError, setYAMLParseError] = React.useState<string>('');
  const hasLogTypes = React.useMemo(() => Boolean(logTypes?.length), [logTypes?.length]);
  const [viewRawYAML, setViewRawYAML] = React.useState(
    Boolean(yamlParseError) ||
      !isConsoleCompatible ||
      type === DetectionType.CorrelationRule ||
      hideRuleBuilder,
  );

  const [hasLoaded, setHasLoaded] = React.useState(false);

  const { loading: fieldsLoading, data } = useGetFlatFilterFields({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        logTypes,
        showUnsafeTypes: true,
      },
    },
    skip: !hasLogTypes,
  });

  const filterFields = React.useMemo(
    () => data?.flatFilterFields?.flatFields || [],
    [data?.flatFilterFields],
  );

  const handleToggleChange = React.useCallback(
    (_, value) => setViewRawYAML(value === 'true'),
    [setViewRawYAML],
  );

  React.useEffect(() => {
    if (!fieldsLoading) {
      setHasLoaded(true);
    }
  }, [fieldsLoading]);

  const displaySimpleFiltering = React.useMemo(() => {
    return type === DetectionType.Rule && (isUpdate || Boolean(urlParams.relatedDetectionId));
  }, [isUpdate, type, urlParams.relatedDetectionId]);

  React.useEffect(() => {
    let formatted;
    setYAMLParseError('');
    if (!isSimpleDetection || type === DetectionType.CorrelationRule) {
      return;
    }

    try {
      formatted = formatDSFAsFilters(simpleDetectionBody);
    } catch (error) {
      setYAMLParseError('There is an error in your syntax.');
    }
    if (!formatted) {
      return;
    }

    const maxDepth = findMaximumDepth(formatted);
    if (maxDepth > MAXIMUM_DEPTH) {
      setYAMLParseError(
        `The GUI builder is disabled for ${analysisTypeToLabel(
          type,
          true,
        )} with more than ${MAXIMUM_DEPTH} nested conditions.`,
      );
    }
    if (!isEqual(formatted, simpleDetectionBodyGUI)) {
      if (viewRawYAML || hideRuleBuilder) {
        // upon editing a simple detection body as yaml, we get here
        // since every edit immediately implies the GUI state != the current formatted state.
        // if the formatting is invalid, we set the GUI value to its
        // default empty state { conjunction: any, ruleBuilderCompatible: false, values: [] }
        // this means we can be in a state where the yaml is valid, but the form
        // doesnt let us submit because the GUI state is invalid.
        setFieldValue('simpleDetectionBodyGUI', formatted);
      } else {
        setFieldValue('simpleDetectionBody', formatFiltersAsDSF(simpleDetectionBodyGUI));
      }
    }
  }, [
    type,
    hideRuleBuilder,
    viewRawYAML,
    simpleDetectionBody,
    simpleDetectionBodyGUI,
    isSimpleDetection,
    setFieldValue,
    setYAMLParseError,
  ]);

  if (type === DetectionType.CorrelationRule) {
    return (
      <>
        <Flex align="center" justify="space-between" mb={6}>
          <Heading variant="h5" wordBreak="break-word">
            <Flex align="center" gap={2} mr={2}>
              Detection
              <Link href={WRITING_CORRELATION_RULES_DOC_URL} external>
                <Icon type="docs" size="medium" />
              </Link>
            </Flex>
          </Heading>
        </Flex>
        <Box>
          <CorrelationRuleEditorV3 readOnly={isManaged || !canEdit} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Flex align="center" justify="space-between" mb={3}>
        <Heading wordBreak="break-word" variant="h5">
          Detect
        </Heading>
        {isSimpleDetection && type === DetectionType.Rule && !hideRuleBuilder ? (
          <ToggleButtonGroup value={String(viewRawYAML)} onChange={handleToggleChange}>
            <TooltipToggleButton
              content={yamlParseError}
              size="medium"
              value="false"
              disabled={Boolean(yamlParseError)}
              pointerEvents="initial"
            >
              {analysisTypeToLabel(type)} Builder
            </TooltipToggleButton>
            <ToggleButton size="medium" value="true">
              Text Editor
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
      </Flex>
      <Box>
        {type === DetectionType.Rule ? (
          <>
            {!hasLogTypes && isSimpleDetection && (
              <Box mb={4}>
                <Alert
                  variant="warning"
                  title={`Please select a log type to update ${
                    isUpdate ? 'filters and ' : ''
                  }${analysisTypeToLabel(type)} clauses.`}
                />
              </Box>
            )}
            {displaySimpleFiltering && (
              <>
                <Flex gap={4}>
                  <Heading variant="h5">Filter to only include events:</Heading>
                  <Tooltip content="Filters process first and can be used to modify the below detection. Click to learn more.">
                    <Link href={RULE_FILTERS_DOC_URL} external>
                      <Icon
                        type="info"
                        size="medium"
                        color={tokens.content.assets.accent[14][2]}
                        cursor="pointer"
                      />
                    </Link>
                  </Tooltip>
                </Flex>
                <Box mb={1} mt={4} pb={4}>
                  <DetectionFieldFiltersFormV3 disabled={!(hasLogTypes && canEdit)} />
                </Box>
                <Heading variant="h6" pb={4}>
                  Match on the following rule logic:
                </Heading>
              </>
            )}
            {isSimpleDetection && (
              <Card
                display="flex"
                alignItems="center"
                p={4}
                borderWidth={0}
                backgroundColor={tokens.background.neutral.inverse.raised[3]}
              >
                {fieldsLoading ? (
                  <Text color={tokens.content.text.accent[14][2]}>Loading...</Text>
                ) : (
                  <Flex direction="column" width="100%">
                    {viewRawYAML || hideRuleBuilder ? (
                      <Box data-testid="function-textarea" flex={1}>
                        <FastField
                          as={FormikEditorV3}
                          placeholder={`# Enter the body of the ${analysisTypeToLabel(
                            type,
                          )} here...`}
                          name="simpleDetectionBody"
                          language="yaml"
                          required
                          aria-describedby="editor-helper-text"
                          readOnly={isManaged || !canEdit || !isConsoleCompatible}
                        />
                      </Box>
                    ) : (
                      <CompoundInputFieldArrayWithJoinsV3
                        preface="Where"
                        name="simpleDetectionBodyGUI"
                        newItem={{
                          target: null,
                          operator: null,
                          value: null,
                        }}
                        showJoinChips
                        allowGroups
                        unitLabel="Clause"
                        maximumNestingDepth={MAXIMUM_DEPTH}
                        disabled={!hasLogTypes || !canEdit}
                      >
                        {(
                          filter: ExtractedFilterCondition,
                          { namePrefix }: CompoundInputFieldArrayMetaProps,
                        ) => (
                          <>
                            <FormikFilterFieldComboboxV3
                              menuTitle="Select a field from the list"
                              logTypes={logTypes}
                              allowJSONPath
                              autoFocus={hasLoaded}
                              name={`${namePrefix}.target`}
                              key={`${namePrefix}.target`}
                              disabled={!hasLogTypes || !canEdit}
                              color={componentTokens.tags.colors.condition.text.labelBlue}
                            />
                            <FilterOperatorComboboxV3
                              name={`${namePrefix}.operator`}
                              key={`${namePrefix}.operator`}
                              disabled={!hasLogTypes || !canEdit}
                              currentOperator={filter?.operator}
                              fieldType={filterFields.find(f => f.target === filter?.target)?.type}
                              placeholder="Condition"
                              color={componentTokens.tags.colors.condition.text.labelGreen}
                            />
                            <FilterValueFieldV3
                              data-tid="no-code-filter-value"
                              name={`${namePrefix}.value`}
                              key={`${namePrefix}.value`}
                              placeholder="Value"
                              disabled={!hasLogTypes || !canEdit}
                              currentOperator={filter?.operator}
                              fieldType={filterFields.find(f => f.target === filter?.target)?.type}
                              color={componentTokens.tags.colors.condition.text.labelBase}
                            />
                          </>
                        )}
                      </CompoundInputFieldArrayWithJoinsV3>
                    )}
                    {/* @ts-ignore */}
                    {errors?.simpleDetectionBodyGUI && (
                      <FormError mt={2} id="simpleDetectionBody-error">
                        {/* @ts-ignore */}
                        {errors?.simpleDetectionBodyGUI}
                      </FormError>
                    )}
                  </Flex>
                )}
              </Card>
            )}
          </>
        ) : null}
        {(type !== DetectionType.Rule || !isSimpleDetection) && (
          <>
            <Box data-testid="function-textarea">
              <FastField
                as={FormikEditorV3}
                placeholder={`# Enter the body of the ${analysisTypeToLabel(type)} here...`}
                name="body"
                language="python"
                required
                aria-describedby="editor-helper-text"
                readOnly={isManaged || !canEdit}
              />
            </Box>
            <FormHelperText id="editor-helper-text" mt={3}>
              Need to define re-usable functions? Define them in a
              <Link ml={1} size="small" as={RRLink} to={urls.build.globalHelpers.list()}>
                global helper
              </Link>
            </FormHelperText>
          </>
        )}
      </Box>
    </>
  );
};

export default React.memo(BaseSimpleDetectionFormEditorSectionV3);
